import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Space,
  Typography,
  Switch,
  Button,
  Tag,
  Alert,
  Input,
  Modal,
  Tooltip,
  message,
} from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useCan } from '@refinedev/core';
import { adminApi } from '@/services/api';
import type { BattingRulesConfigResponse } from '@/types';
import {
  BATTING_RULES_SWITCHES,
  formatBattingRules,
  parseBattingRules,
  setBattingRulesSwitch,
  splitServerErrors,
} from './battingRulesJson';

const { Title, Text } = Typography;
const { confirm } = Modal;

type ApiError = {
  response?: { status?: number; data?: { message?: string | string[] } };
};

const apiMessage = (error: unknown): string | undefined => {
  const detail = (error as ApiError)?.response?.data?.message;
  return Array.isArray(detail) ? detail.join('; ') : detail;
};

/** 400 = the document failed validation, 422 = the request body was malformed. */
const isRejection = (error: unknown): boolean => {
  const status = (error as ApiError)?.response?.status;
  return status === 400 || status === 422;
};

/**
 * GDD batting rules document, served to every client in /v1/config as
 * `battingRules`. PUT replaces the whole document; null resets it to the
 * server's built-in copy (identical to the copy bundled with the game).
 */
export const BattingRulesTab: React.FC = () => {
  const { data: canEdit } = useCan({ resource: 'config', action: 'edit' });
  const editable = canEdit?.can !== false;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [served, setServed] = useState<BattingRulesConfigResponse | null>(null);
  const [text, setText] = useState('');
  // The server's validation errors for the last rejected save, and the text
  // that was sent, so the list can be marked stale once the admin edits.
  const [rejection, setRejection] = useState<{ text: string; errors: string[] } | null>(null);

  const load = useCallback((res: BattingRulesConfigResponse) => {
    setServed(res);
    setText(formatBattingRules(res.battingRules));
    setRejection(null);
  }, []);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await adminApi.getBattingRules();
      load(res.data);
    } catch (error) {
      setLoadError(true);
      message.error(apiMessage(error) ?? 'Failed to load the batting rules');
    } finally {
      setLoading(false);
    }
  }, [load]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  // Nothing is saved until the served document has loaded, so a save can't
  // replace rules that were never shown.
  const ready = served !== null;
  const parsed = useMemo(() => parseBattingRules(text), [text]);
  const servedText = served ? formatBattingRules(served.battingRules) : '';
  const dirty = ready && text !== servedText;
  const rejectionIsStale = rejection !== null && rejection.text !== text;

  const save = (battingRules: Record<string, unknown> | null) => {
    const sentText = text;
    confirm({
      title:
        battingRules === null
          ? 'Reset the batting rules to the built-in copy?'
          : 'Replace the batting rules?',
      icon: <ExclamationCircleOutlined />,
      content:
        battingRules === null
          ? 'The server goes back to its built-in copy, which matches the copy bundled with the game. Every edit made here is discarded.'
          : 'Every client gets the new rules on its next config fetch. A document the game rejects makes it fall back to its bundled copy.',
      onOk: async () => {
        setSaving(true);
        setRejection(null);
        try {
          const res = await adminApi.updateBattingRules(battingRules);
          load(res.data);
          message.success(battingRules === null ? 'Batting rules reset' : 'Batting rules saved');
        } catch (error) {
          const detail = apiMessage(error);
          if (battingRules !== null && isRejection(error) && detail) {
            setRejection({ text: sentText, errors: splitServerErrors(detail) });
            message.error('The server rejected the batting rules');
          } else {
            message.error(
              detail ??
                (battingRules === null
                  ? 'Failed to reset the batting rules'
                  : 'Failed to save the batting rules')
            );
          }
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const discard = () => {
    confirm({
      title: 'Discard your changes?',
      icon: <ExclamationCircleOutlined />,
      onOk: () => {
        setText(servedText);
        setRejection(null);
      },
    });
  };

  if (loadError && !ready) {
    return (
      <Card>
        <Alert
          type="error"
          showIcon
          message="Couldn't load the batting rules"
          description="Nothing can be changed until the served document has loaded."
          action={<Button onClick={fetchRules}>Retry</Button>}
        />
      </Card>
    );
  }

  return (
    <Card loading={loading}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={5}>Batting rules (GDD)</Title>
          <Text type="secondary">
            The GDD batting tables (zone modifiers, composure, shot accuracy and strength) the game
            uses in every match. Served in the game&apos;s config; clients pick up a change on their
            next config fetch.
          </Text>
        </div>

        <Space wrap>
          {served?.isDefault ? (
            <Tooltip title="The server is serving its built-in copy, which matches the copy bundled with the game.">
              <Tag color="green">Built-in copy</Tag>
            </Tooltip>
          ) : (
            <Tag color="orange">Edited</Tag>
          )}
          {served?.updatedAt && (
            <Text type="secondary">
              Last updated: {new Date(served.updatedAt).toLocaleString()}
            </Text>
          )}
          {dirty && <Tag color="gold">Unsaved changes</Tag>}
        </Space>

        <Space direction="vertical" size="small">
          {BATTING_RULES_SWITCHES.map((s) => {
            const value = parsed.ok ? parsed.doc[s.key] === true : false;
            return (
              <Space key={s.key}>
                <Switch
                  checked={value}
                  disabled={!editable || !ready || !parsed.ok}
                  onChange={(checked) => {
                    const next = setBattingRulesSwitch(text, s.key, checked);
                    if (next !== null) setText(next);
                  }}
                />
                <Text strong>{s.label}</Text>
                <Text type="secondary">{s.help}</Text>
              </Space>
            );
          })}
        </Space>

        <div>
          <Text strong>Document (JSON)</Text>
          <Input.TextArea
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={!editable || !ready}
            autoSize={{ minRows: 16, maxRows: 32 }}
            spellCheck={false}
            style={{ fontFamily: 'monospace', fontSize: 12, marginTop: 8 }}
          />
        </div>

        {ready && !parsed.ok && <Alert type="error" showIcon message={parsed.error} />}
        {rejection && (
          <Alert
            type={rejectionIsStale ? 'warning' : 'error'}
            showIcon
            message={
              rejectionIsStale
                ? 'The server rejected your last save. You have edited the document since; save again to re-check.'
                : 'The server rejected the document'
            }
            description={
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {rejection.errors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            }
          />
        )}

        <Space wrap>
          <Button
            type="primary"
            onClick={() => parsed.ok && save(parsed.doc)}
            loading={saving}
            disabled={!editable || !ready || !parsed.ok || !dirty}
          >
            Save batting rules
          </Button>
          <Button onClick={discard} disabled={!dirty || saving}>
            Discard changes
          </Button>
          <Button
            danger
            onClick={() => save(null)}
            disabled={!editable || !ready || saving || served?.isDefault}
          >
            Reset to built-in copy
          </Button>
        </Space>
      </Space>
    </Card>
  );
};
